import { BodyText } from '@servicetitan/design-system';
import { FC } from 'react';
import { SelectOption } from '../../../utils/form';
import { FilterTag } from '../../filter-tag/filter-tag';
import { FakeInput } from '../input/fake-input';

export interface PortalSelectTriggerOpts {
    className?: string;
    clearable?: boolean;
    placeholder?: string;
    height?: number;
    title?: string;
}

export interface PortalSelectTriggerViewProps<T, TD> {
    opts: SelectOption<T, TD>[];
}

export interface PortalSelectTriggerProps<T, TD> {
    opts?: PortalSelectTriggerOpts;
    title?: string;
    selectedOptions: SelectOption<T, TD>[];
    searchOpen: boolean;
    multiple?: boolean;
    disabled?: boolean;
    viewComponent?: FC<PortalSelectTriggerViewProps<T, TD>>;
    openSearch(): void;
    closeSearch(): void;
    updateValues?(values: T[]): void;
}

function DefaultViewComponent<T, TD>({ opts }: PortalSelectTriggerViewProps<T, TD>) {
    return (
        <BodyText>
            {opts.length === 0
                ? `0 selected`
                : opts.length === 1
                  ? opts[0]?.text
                  : `${opts.length} selected`}
        </BodyText>
    );
}

export function PortalSelectTriggerInput<T, TD>({
    closeSearch,
    disabled,
    openSearch,
    opts,
    searchOpen,
    selectedOptions,
    updateValues,
    viewComponent,
}: PortalSelectTriggerProps<T, TD>) {
    const onClearValues = () => {
        if (opts?.clearable && updateValues) {
            updateValues([]);
        }
        closeSearch();
    };

    const ViewComponent = viewComponent ?? DefaultViewComponent;

    return (
        <FakeInput
            clearable={!!opts?.clearable && !!selectedOptions.length}
            active={searchOpen}
            height={opts?.height}
            disabled={disabled}
            rightIconName={searchOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            empty={!selectedOptions.length}
            onClick={searchOpen ? closeSearch : openSearch}
            onClear={onClearValues}
        >
            {selectedOptions.length ? (
                <ViewComponent opts={selectedOptions} />
            ) : (
                (opts?.placeholder ?? `Select`)
            )}
        </FakeInput>
    );
}

export function PortalSelectTriggerTag<T, TD>({
    closeSearch,
    disabled,
    multiple,
    openSearch,
    searchOpen,
    selectedOptions,
    title,
}: PortalSelectTriggerProps<T, TD>) {
    return (
        <FilterTag
            active={!!selectedOptions.length}
            count={multiple ? selectedOptions.length : undefined}
            disabled={disabled}
            rightIconName={searchOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            onClick={searchOpen ? closeSearch : openSearch}
        >
            {title}
        </FilterTag>
    );
}
